import type { ApolloClient } from '@apollo/client';
import { isNumber, orderBy, uniqBy } from 'lodash';
import queryString from 'query-string';

import type { InspectionPicture } from 'horizon/routes/Inspections/types';
import {
  type AtlasGqlGetPictureQuery,
  type AtlasGqlGetPictureQueryVariables,
  type AtlasGqlHorizonDamageObservation,
  type AtlasGqlHorizonDamageObservationGroup,
  GetPictureDocument,
} from 'types/atlas-graphql';
import { findPreviousDayItem } from 'utils/inspectionOrder';

import {
  getPreviousObservationGroup,
  getPrimaryObservationForObservationGroup,
  resolvePrimaryObservationGroup,
} from '../utils';

import type { CompareTarget, PreviousDamageInfo, PreviousInspectionInfo } from './types';

export type FetchedPicture = AtlasGqlGetPictureQuery['picture'];

/**
 * Fetches one picture and resolves to `undefined` if it fails. Not a generated lazy-query hook:
 * each execute of one cancels the previous, so pictures fetched together would abort each other.
 */
export const fetchPicture = async (
  client: ApolloClient,
  pictureId: string | undefined
): Promise<FetchedPicture> => {
  if (!pictureId) {
    return undefined;
  }

  try {
    const { data } = await client.query<AtlasGqlGetPictureQuery, AtlasGqlGetPictureQueryVariables>({
      query: GetPictureDocument,
      variables: { pictureId },
    });

    return data?.picture;
  } catch {
    return undefined;
  }
};

export type CompareData = {
  previousDamageInfo?: PreviousDamageInfo;
  prevInspectionDetails?: PreviousInspectionInfo;
};

/**
 * Specializes group-level compare data to a single thumbnail so that Compare opens the clicked
 * observation's image on the left, while keeping the resolved previous (right) image unchanged.
 * If the thumbnail has no observation/picture id, the group-level current ids are left intact.
 */
export const getThumbnailCompareData = (
  groupCompareData: CompareData | undefined,
  observation: AtlasGqlHorizonDamageObservation | null | undefined
): CompareData => {
  const currentObservationOverride = {
    ...(observation?.id ? { currentObservationId: observation.id } : {}),
    ...(observation?.picture?.id ? { currentPictureId: observation.picture.id } : {}),
  };

  return {
    previousDamageInfo: groupCompareData?.previousDamageInfo
      ? { ...groupCompareData.previousDamageInfo, ...currentObservationOverride }
      : undefined,
    prevInspectionDetails: groupCompareData?.prevInspectionDetails
      ? { ...groupCompareData.prevInspectionDetails, ...currentObservationOverride }
      : undefined,
  };
};

export const getSelectedCompareData = (
  observationGroup: AtlasGqlHorizonDamageObservationGroup | null | undefined,
  compareTarget: CompareTarget | null
): CompareData => {
  const sourceObservation = getPrimaryObservationForObservationGroup(observationGroup);

  if (!compareTarget || !sourceObservation?.picture?.id) {
    return {};
  }

  return {
    previousDamageInfo: {
      currentPictureId: sourceObservation.picture.id,
      currentObservationId: sourceObservation.id,
      previousPictureId: compareTarget.pictureId,
      previousObservationId: compareTarget.observationId,
    },
  };
};

export const getPreviousDamageDetails = (
  observationGroups: AtlasGqlHorizonDamageObservationGroup[]
) => {
  if ((observationGroups?.length ?? 0) <= 1) {
    return null;
  }

  const primaryGroup = resolvePrimaryObservationGroup(observationGroups);
  const previousGroup = getPreviousObservationGroup(observationGroups, primaryGroup);

  if (!previousGroup) {
    return null;
  }

  const prevGroupSourceObservation = getPrimaryObservationForObservationGroup(previousGroup);
  const currentGroupSourceObservation = getPrimaryObservationForObservationGroup(primaryGroup);

  if (!prevGroupSourceObservation || !prevGroupSourceObservation?.picture?.id) {
    return null;
  }

  return {
    previousPictureId: prevGroupSourceObservation.picture.id,
    previousObservationId: prevGroupSourceObservation.id,
    currentObservationId: currentGroupSourceObservation?.id,
    currentPictureId: currentGroupSourceObservation?.picture?.id,
  };
};

export const getPreviousInspectionDetails = (
  sourceObservation: AtlasGqlHorizonDamageObservation
) => {
  const inspectionAsset = sourceObservation?.inspection?.asset;
  const assetInspections = inspectionAsset?.inspections?.nodes ?? [];

  // if the inspection is on a Turbine, also include inspections from its child Blades
  const childBladeInspections =
    inspectionAsset?.__typename === 'Turbine'
      ? (inspectionAsset.children ?? []).flatMap((child) => child?.inspections?.nodes ?? [])
      : [];

  // if the inspection is on a Blade, also include inspections from its parent Turbine
  const parentTurbineInspections =
    inspectionAsset?.__typename === 'Blade'
      ? (inspectionAsset.parent?.inspections?.nodes ?? [])
      : [];

  // combined inspections and deduplicate
  const allInspections = uniqBy(
    [...assetInspections, ...childBladeInspections, ...parentTurbineInspections],
    'id'
  );

  const currentInspection = allInspections.find(
    (inspection) => inspection?.id === sourceObservation?.inspection?.id
  );

  if (!currentInspection) {
    return null;
  }

  const previousInspection = findPreviousDayItem(
    allInspections,
    currentInspection,
    (inspection) => inspection
  );

  if (!previousInspection) {
    return null;
  }

  const { radialDistance, bladeAsset, bladeSide } = sourceObservation?.picture?.target ?? {};

  return {
    inspectionId: previousInspection.id,
    inspectionDate: previousInspection.inspectionDate,
    blade: bladeAsset?.position,
    bladeSide,
    radialDistance,
    currentObservationId: sourceObservation.id,
    currentPictureId: sourceObservation.picture?.id,
  };
};

export const getMatchingPictureId = (
  pictures: InspectionPicture[] | undefined,
  radialDistance: number
) => {
  if (!pictures?.length) {
    return;
  }

  // Order ascending by radial distance so that, on a tie, the lower (rounded-down) picture wins.
  const orderedPictures = orderBy(pictures, ['target.radialDistance'], ['asc']);

  // Select the picture whose radial distance is closest to the damage's radial distance.
  // Ties resolve to the lower distance because we iterate in ascending order and only replace
  // the running best on a strictly smaller delta.
  const closestPicture = orderedPictures.reduce<InspectionPicture | undefined>((closest, pic) => {
    const picDistance = pic?.target?.radialDistance;

    if (!isNumber(picDistance)) {
      return closest;
    }

    const closestDistance = closest?.target?.radialDistance;

    if (!isNumber(closestDistance)) {
      return pic;
    }

    return Math.abs(picDistance - radialDistance) < Math.abs(closestDistance - radialDistance)
      ? pic
      : closest;
  }, undefined);

  return closestPicture?.id ?? orderedPictures[0]?.id;
};

export const getComparePicturesPath = ({
  currentPictureId,
  currentObservationId,
  previousPictureId,
  previousObservationId,
  emptyPreviousPicture,
}: {
  currentPictureId: string;
  currentObservationId: string;
  previousPictureId?: string;
  previousObservationId?: string;
  emptyPreviousPicture?: boolean;
}) =>
  `/pictures/${currentPictureId}?${queryString.stringify({
    observation: currentObservationId,
    picture2: previousPictureId,
    observation2: previousObservationId,
    emptyPreviousPicture,
  })}`;
